import {
  Banner,
  Checkbox,
  collapseColumn,
  column,
  ContentHeader,
  Css,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridTableLayout,
  Icon,
  numericColumn,
  Palette,
  simpleHeader,
  useComputed,
  useGridTableLayoutState,
} from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useMemo } from "react";
import { emptyCellDash } from "src/components";
import {
  createCheckoutStepSections,
  getDisabledOptionIds,
  getUnsatisfiedPrerequisites,
  isRequiredWhenMet,
} from "src/routes/components/ProductOfferingConfigUtils";
import { formatCentsToPrice, formatList, idsOf, pluralize, queryResult } from "src/utils";
import { formatMonthDayYear } from "src/utils/dates";
import { ApplyConfigurationFormInput } from "../applyConfigurationForm";
import {
  SelectChangesStep_ChangeEventConfigChangeFragment,
  SelectChangesStep_OptionFragment,
  SelectChangesStep_OptionGroupChangeFragment,
  useSelectChangesStepQuery,
} from "./2.SelectChangesStep.generated";

type SelectChangesStepProps = { formState: ObjectState<ApplyConfigurationFormInput> };

export function SelectChangesStep(props: SelectChangesStepProps) {
  const { formState } = props;
  const changeEventId = useComputed(() => formState.id.value, [formState]);
  const query = useSelectChangesStepQuery({ variables: { changeEventId: changeEventId! } });
  return queryResult(query, ({ changeEvent }) => (
    <SelectChangesStepView formState={formState} configChange={changeEvent.configChange!} />
  ));
}

type SelectChangesStepViewProps = Pick<SelectChangesStepProps, "formState"> & {
  configChange: SelectChangesStep_ChangeEventConfigChangeFragment;
};

function SelectChangesStepView(props: SelectChangesStepViewProps) {
  const { formState, configChange } = props;
  const changes = configChange.optionGroupChanges;
  const targetName = configChange.target?.name ?? "the selected configuration";
  const layoutState = useGridTableLayoutState<Record<string, unknown>>({ search: "client" });
  const optionIds = useComputed(() => formState.optionIds.value ?? [], [formState]);

  const applied = useMemo(() => appliedById(changes, optionIds), [changes, optionIds]);
  const tableRows = useMemo(() => createRows(changes, optionIds), [changes, optionIds]);
  const columns = useMemo(
    () =>
      createColumns({
        onSelect: (change, applied) => {
          formState.optionIds.set(selectChange(changes, formState.optionIds.value ?? [], change.id, applied));
        },
        // A partly-applied section applies the rest, like a table's select-all, skipping the rows held in place
        onSelectSection: (sectionChanges, applied) => {
          formState.optionIds.set(
            sectionChanges.reduce(
              (ids, change) =>
                lockedBy(changes, change, ids).nonEmpty ? ids : selectChange(changes, ids, change.id, applied),
              formState.optionIds.value ?? [],
            ),
          );
        },
      }),
    [changes, formState],
  );
  const hasPastCutoff = changes.some((c) => c.isPastCutoff && applied.get(c.id));

  return (
    <>
      <ContentHeader
        tooltip="Only changes from the selected configuration are shown. To add or modify other options, use the Add an On-Book Option workflow."
        title="Select Option Changes to Apply"
        description={`${changes.length} ${pluralize(changes.length, "difference was", "differences were")} found between your current configuration and ${targetName}. Select the changes that you want to apply to this project. `}
      />
      {hasPastCutoff && (
        <Banner
          type="warning"
          message={
            <span>
              <strong>Past Cutoff Dates</strong> — Some of this work may already be complete or past its cutoff. Confirm
              that this change can still be made before continuing.
            </span>
          }
        />
      )}
      <GridTableLayout
        layoutState={layoutState}
        hideEditColumns
        emptyFallback="No differences found"
        tableProps={{
          rows: tableRows,
          columns,
          style: { rowHeight: "fixed" },
          rowStyles: { section: { cellCss: Css.bgGray50.$ } },
        }}
      />
    </>
  );
}

type Change = SelectChangesStep_OptionGroupChangeFragment;
type Option = SelectChangesStep_OptionFragment["readyPlanOption"];

type SectionRowData = { title: string; changes: Change[]; selected: boolean | "indeterminate"; disabled: boolean };
type ChangeRowData = { change: Change; applied: boolean; lockReason: string | undefined; flag: string | undefined };

type Row =
  | { kind: "header"; id: string; data: undefined }
  | { kind: "section"; id: string; data: SectionRowData }
  | { kind: "change"; id: string; data: ChangeRowData };

function createRows(changes: Change[], optionIds: readonly string[]): GridDataRow<Row>[] {
  const applied = appliedById(changes, optionIds);
  const sections = createCheckoutStepSections(
    changes.map((change) => ({ order: change.optionGroup.order, step: change.optionGroup.step, change })),
  );
  return [
    simpleHeader,
    ...sections.map((section) => {
      const sectionChanges = section.optionGroups.map((og) => og.change);
      const appliedCount = sectionChanges.count((c) => !!applied.get(c.id));
      return {
        kind: "section" as const,
        id: `section:${section.code}`,
        data: {
          title: section.title,
          changes: sectionChanges,
          selected:
            appliedCount === sectionChanges.length ? true : appliedCount === 0 ? false : ("indeterminate" as const),
          // Nothing to toggle when a prerequisite holds every row in place
          disabled: sectionChanges.every((c) => lockedBy(changes, c, optionIds).nonEmpty),
        },
        children: sectionChanges.map((change) => {
          const locks = lockedBy(changes, change, optionIds);
          return {
            kind: "change" as const,
            id: change.id,
            data: {
              change,
              applied: !!applied.get(change.id),
              lockReason: locks.nonEmpty
                ? `This option is required when ${quoteOptions(locks)} is selected.`
                : undefined,
              flag: rowFlag(changes, change, optionIds),
            },
          };
        }),
      };
    }),
  ];
}

type ColumnActions = {
  onSelect: (change: Change, applied: boolean) => void;
  onSelectSection: (changes: Change[], applied: boolean) => void;
};

function createColumns(actions: ColumnActions): GridColumn<Row>[] {
  const { onSelect, onSelectSection } = actions;
  return [
    collapseColumn<Row>({ change: emptyCell }),
    column<Row>({
      id: "apply",
      header: "Apply",
      section: ({ title, changes, selected, disabled }) => (
        <Checkbox
          checkboxOnly
          label={`Apply ${title}`}
          selected={selected}
          disabled={disabled}
          onChange={() => onSelectSection(changes, selected !== true)}
          data-testid="applySection"
        />
      ),
      change: ({ change, applied, lockReason }) => (
        <Checkbox
          checkboxOnly
          label={`Apply ${change.optionGroup.name}`}
          selected={applied}
          disabled={lockReason}
          onChange={(value) => onSelect(change, value)}
          data-testid="apply"
        />
      ),
      w: "64px",
    }),
    column<Row>({
      id: "option",
      header: "Option",
      section: ({ title }) => ({ content: <span css={Css.smSb.$}>{title}</span>, value: title }),
      change: ({ change, flag }) => ({
        content: <OptionCell name={change.optionGroup.name} flag={flag} />,
        value: change.optionGroup.name,
      }),
      w: 2,
    }),
    column<Row>({
      id: "location",
      header: "Location",
      section: emptyCell,
      change: ({ change }) => {
        const rpl = change.optionGroup.readyPlanLocation;
        return rpl?.roomType?.name ?? rpl?.location.name ?? emptyCellDash;
      },
    }),
    column<Row>({
      id: "currentValue",
      header: "Current Value",
      section: emptyCell,
      change: ({ change }) => change.originalOption?.readyPlanOption.name ?? emptyCellDash,
      w: 1.5,
    }),
    column<Row>({
      id: "newValue",
      header: "New Value",
      section: emptyCell,
      change: ({ change }) => change.targetOption?.readyPlanOption.name ?? emptyCellDash,
      w: 1.5,
    }),
    column<Row>({
      id: "cutoffDate",
      header: "Cutoff Date",
      section: emptyCell,
      change: ({ change }) => ({
        content: change.cutoffDate ? (
          <span css={Css.if(change.isPastCutoff).red600.$}>{formatMonthDayYear(change.cutoffDate)}</span>
        ) : (
          emptyCellDash
        ),
        value: change.cutoffDate ? formatMonthDayYear(change.cutoffDate) : emptyCellDash,
      }),
      align: "right",
    }),
    numericColumn<Row>({
      id: "currentPrice",
      header: "Current Price",
      section: emptyCell,
      change: ({ change }) => optionPrice(change.originalOption),
    }),
    numericColumn<Row>({
      id: "newPrice",
      header: "New Price",
      section: emptyCell,
      change: ({ change }) => optionPrice(change.targetOption),
    }),
  ];
}

type OptionCellProps = { name: string; flag: string | undefined };

/** The option group's name, flagged with what it requires or conflicts with. Why a row is locked is on its checkbox. */
function OptionCell(props: OptionCellProps) {
  const { name, flag } = props;
  return (
    <div css={Css.df.aic.gap1.oh.$}>
      <span css={Css.truncate.$} title={name}>
        {name}
      </span>
      {flag && (
        <span css={Css.fs0.df.$}>
          <Icon icon="error" inc={2} color={Palette.Yellow600} tooltip={flag} />
        </span>
      )}
    </div>
  );
}

/** Whether each change is applied, keyed by change id. A change is applied when `optionIds` holds its target option and not its original one. */
function appliedById(changes: Change[], optionIds: readonly string[]): Map<string, boolean> {
  return new Map(
    changes.map((change) => {
      const [target] = sideOptions(change, true);
      const [original] = sideOptions(change, false);
      return [change.id, (!target || optionIds.includes(target.id)) && (!original || !optionIds.includes(original.id))];
    }),
  );
}

/** Swaps in the change's target options (or its original ones when not applied), leaving every other option alone. */
function setApplied(optionIds: readonly string[], change: Change, applied: boolean): string[] {
  const moving = [...sideOptions(change, true), ...sideOptions(change, false)];
  return [...optionIds.difference(idsOf(moving)), ...idsOf(sideOptions(change, applied))].unique();
}

/**
 * Checks or unchecks a row, then updates the other rows it affects so the selection stays valid:
 * 1. Anything the row now requires gets checked (or unchecked) to match.
 * 2. Anything the move broke -- a row that now conflicts with the selection, or lost a prerequisite it had -- gets
 *    flipped too. Otherwise the BE would quietly drop it on save.
 *
 * Every row that moves can affect others in turn, so this repeats for each of them. A row only moves once, which
 * means the row the user clicked always keeps what they picked, and two rows can never keep flipping each other.
 *
 * For example, with a fireplace that requires a gas connection, and a covered patio that conflicts with the fireplace:
 * - checking the fireplace also checks the gas connection, and unchecks the covered patio;
 * - checking the covered patio again unchecks the fireplace, and the gas connection stays checked (nothing conflicts
 *   with it, it's just no longer required).
 */
function selectChange(changes: Change[], optionIds: readonly string[], changeId: string, applied: boolean): string[] {
  const byId = changes.keyBy((c) => c.id);
  const current = appliedById(changes, optionIds);
  // Only a row this click breaks is flipped, so one that was already off (e.g. an inactive option) stays put
  const validBefore = changes.filter((c) => isSideValid(c, !!current.get(c.id), optionIds));
  const moved = new Set<string>();
  let ids = [...optionIds];

  function set(id: string, value: boolean) {
    const change = byId[id];
    if (!change || moved.has(id) || current.get(id) === value) return;
    moved.add(id);
    current.set(id, value);
    ids = setApplied(ids, change, value);
    unmetNeeds(changes, change, value, ids).forEach((need) => set(need.change.id, need.applied));
    validBefore
      .filter((other) => !moved.has(other.id) && !isSideValid(other, !!current.get(other.id), ids))
      .forEach((other) => set(other.id, !current.get(other.id)));
  }

  set(changeId, applied);
  return ids;
}

/** A row on one of its sides: its target option when applied, its original one otherwise. */
type Side = { change: Change; applied: boolean };

/** The row's option on `applied`, as a list (empty for an ADD's original or a REMOVE's target) for the set math. */
function sideOptions(change: Change, applied: boolean): Option[] {
  const option = applied ? change.targetOption : change.originalOption;
  return option ? [option.readyPlanOption] : [];
}

/** Every side of every row other than `except` that holds any of `ids`, i.e. where a rule's options live in this table. */
function sidesHolding(changes: Change[], except: Change, ids: readonly string[]): Side[] {
  return changes
    .filter((change) => change !== except)
    .flatMap((change) =>
      [true, false]
        .filter((applied) => ids.intersection(idsOf(sideOptions(change, applied))).nonEmpty)
        .map((applied) => ({ change, applied })),
    );
}

/** The options among `ids` that other rows hold, named as those rows have them. */
function optionsHeld(changes: Change[], except: Change, ids: readonly string[]): Option[] {
  return sidesHolding(changes, except, ids)
    .flatMap(({ change, applied }) => sideOptions(change, applied))
    .filter((o) => ids.includes(o.id))
    .uniqueBy((o) => o.id);
}

/** Whether the selection requires the row's group, per its `requiredWhen`. */
function isGroupRequired(change: Change, selectedOptionIds: readonly string[]): boolean {
  const { requiredWhen } = change.optionGroup;
  // A `none` clause reads as a conflict rather than a prerequisite, so it's left to the BE to reconcile on save
  if (!requiredWhen || requiredWhen.some((clause) => clause.none.nonEmpty)) return false;
  return isRequiredWhenMet(requiredWhen, new Set(selectedOptionIds));
}

/** The options that turn the row's group on, i.e. the ones its `requiredWhen` names. */
function gateIds(change: Change): string[] {
  return idsOf((change.optionGroup.requiredWhen ?? []).flatMap((clause) => clause.all));
}

/**
 * Whether the row can sit on `applied` alongside the rest of the selection: none of that side's options conflicts
 * with, or is missing a prerequisite from, the other options, and an empty side isn't one the selection requires.
 * `getDisabledOptionIds` never flags a selected option, hence judging the side against everything else.
 */
function isSideValid(change: Change, applied: boolean, optionIds: readonly string[]): boolean {
  const options = sideOptions(change, applied);
  const others = optionIds.difference(idsOf(options));
  if (getDisabledOptionIds(options, others).nonEmpty) return false;
  return options.nonEmpty || !isGroupRequired(change, others);
}

/**
 * What the row's `applied` side needs that the selection doesn't hold, as the rows and sides to pull in: a
 * prerequisite is only the user's to settle when exactly one side of one other row holds it (as with checkout, any
 * option of a prerequisite's group satisfies it), and a group the side makes required is needed on whichever of its
 * sides has options.
 */
function unmetNeeds(changes: Change[], change: Change, applied: boolean, optionIds: readonly string[]): Side[] {
  const selected = [...optionIds, ...idsOf(sideOptions(change, applied))].unique();
  const prerequisites = sideOptions(change, applied)
    .flatMap((o) => getUnsatisfiedPrerequisites(o, new Set(selected)))
    .map((prereqIds) => sidesHolding(changes, change, prereqIds))
    .filter((sides) => sides.length === 1)
    .map((sides) => sides[0]);
  const requiredGroups = changes
    .filter((other) => other !== change && isGroupRequired(other, selected))
    // A row holding nothing on its current side has an empty side, so the one with options is the one to pull in
    .filter(
      (other) => selected.intersection(idsOf([...sideOptions(other, true), ...sideOptions(other, false)])).isEmpty,
    )
    .map((other) => ({ change: other, applied: !!other.targetOption }));
  return [...prerequisites, ...requiredGroups];
}

/**
 * The options holding the change on its current side: the ones on other rows that would be left invalid if it
 * flipped, and the ones turning its group on when flipping would leave the group empty.
 */
function lockedBy(changes: Change[], change: Change, optionIds: readonly string[]): Option[] {
  const current = appliedById(changes, optionIds);
  const applied = !!current.get(change.id);
  const flipped = setApplied(optionIds, change, !applied);
  const dependents = changes
    .filter((other) => other !== change)
    .filter((other) => {
      const otherApplied = !!current.get(other.id);
      return isSideValid(other, otherApplied, optionIds) && !isSideValid(other, otherApplied, flipped);
    })
    .flatMap((other) => sideOptions(other, !!current.get(other.id)));
  const gates =
    sideOptions(change, !applied).isEmpty && isGroupRequired(change, flipped)
      ? optionsHeld(changes, change, gateIds(change)).filter((o) => optionIds.includes(o.id))
      : [];
  return [...dependents, ...gates];
}

/** What the change requires on its current side, or what its other side would conflict with. */
function rowFlag(changes: Change[], change: Change, optionIds: readonly string[]): string | undefined {
  const isApplied = !!appliedById(changes, optionIds).get(change.id);
  const side = sideOptions(change, isApplied);
  const requires = [
    ...optionsHeld(
      changes,
      change,
      side.flatMap((o) => idsOf(o.optionPrerequisites)),
    ),
    // A group this side turns on is needed just like a prerequisite
    ...changes
      .filter((other) => other !== change && idsOf(side).intersection(gateIds(other)).nonEmpty)
      .filter((other) => isGroupRequired(other, optionIds))
      .flatMap((other) => sideOptions(other, !!other.targetOption)),
  ];
  if (requires.nonEmpty) return `This option requires ${quoteOptions(requires)} when selected.`;
  const otherSide = sideOptions(change, !isApplied);
  const conflictIds = otherSide.flatMap((o) => idsOf([...o.optionConflicts, ...o.optionConflictChildren]));
  const conflicts = optionsHeld(changes, change, optionIds.intersection(conflictIds));
  if (conflicts.isEmpty) return undefined;
  return `To select the ${isApplied ? "original" : "new"} option, unselect the conflict ${quoteOptions(conflicts)}.`;
}

/** I.e. "‘Gas Connection’ and ‘Surround Material’" */
function quoteOptions(options: Option[]): string {
  return formatList(options.map((o) => `‘${o.name}’`).unique());
}

/**
 * Mirrors checkout's `formatDeltaPriceDisplay`: the option's reserved price is the delta over the group's included
 * option, so no delta reads as "Included" (on either side), and anything else is signed.
 */
function optionPrice(option: SelectChangesStep_OptionFragment | null | undefined) {
  if (!option) return emptyCellDash;
  const priceInCents = option.reservedPriceInCents ?? 0;
  if (priceInCents === 0) return { content: <span css={Css.gray600.$}>Included</span>, value: "Included" };
  return formatCentsToPrice(priceInCents, { trim: true, displayDirection: true });
}
